/**
 * SellerDrawer — seller detail + admin actions (STORY-008-007). `BottomSheet`
 * (clones the shipped seller-applications `DetailDrawer`). Shows the seller
 * profile, a verification-status pill, and permission-gated actions:
 *   - Edit       (marketplace.seller.edit)   → inline RHF form (PATCH)
 *   - Suspend    (marketplace.seller.suspend) → SuspendDialog (hidden if already suspended)
 *   - Restore    (marketplace.seller.edit)   → POST /restore (shown if suspended)
 *   - Notify     (marketplace.seller.edit)   → NotifyDialog (Track AD3, ADR-044)
 *
 * Server RBAC is the real enforcement; hiding here is UX (`useRequirePermission`).
 */
import { useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { BottomSheet, Button, GPill, Skeleton } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import { useRequirePermission } from "@/features/admin/hooks/useRequirePermission";
import { verificationTone } from "@/features/admin/components/tone";
import {
  usePatchSeller,
  useRestoreSeller,
  useSeller,
} from "@/features/admin/sellers/hooks";
import { SellerEditForm } from "@/features/admin/sellers/SellerEditForm";
import { SuspendDialog } from "@/features/admin/sellers/SuspendDialog";
import { NotifyDialog } from "@/features/admin/sellers/NotifyDialog";

export interface SellerDrawerProps {
  sellerId: string | null;
  onClose: () => void;
}

function FieldRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}): ReactElement {
  return (
    <div className="flex flex-col gap-0.5 py-1.5">
      <dt className="text-xs font-medium text-[var(--g-secondary)]">{label}</dt>
      <dd className="text-sm">{value ?? "—"}</dd>
    </div>
  );
}

type DialogMode = "suspend" | "notify" | null;

export function SellerDrawer({
  sellerId,
  onClose,
}: SellerDrawerProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const isDesktop = useIsDesktop();

  const [editing, setEditing] = useState(false);
  const [dialog, setDialog] = useState<DialogMode>(null);

  const { data: seller, isLoading } = useSeller(sellerId);
  const patch = usePatchSeller();
  const restore = useRestoreSeller();

  const canEdit = useRequirePermission("marketplace.seller.edit");
  const canSuspend = useRequirePermission("marketplace.seller.suspend");

  // Reset transient state when the drawer closes.
  useEffect(() => {
    if (sellerId === null) {
      setEditing(false);
      setDialog(null);
    }
  }, [sellerId]);

  const isOpen = sellerId !== null;
  const isSuspended = seller?.verification_status === "suspended";

  const handleEditSubmit = (body: Parameters<typeof patch.mutate>[0]["body"]): void => {
    if (!seller) return;
    patch.mutate(
      { id: seller.id, body },
      {
        onSuccess: () => {
          toast.success(t("sellers.edit.success"));
          setEditing(false);
        },
        onError: () => toast.error(t("error.generic")),
      },
    );
  };

  const handleRestore = (): void => {
    if (!seller) return;
    restore.mutate(seller.id, {
      onSuccess: () => toast.success(t("sellers.restore.success")),
      onError: () => toast.error(t("error.generic")),
    });
  };

  return (
    <>
      <BottomSheet
        open={isOpen}
        onClose={onClose}
        title={t("sellers.detail.title")}
        desktop={isDesktop}
      >
        {isLoading || !seller ? (
          <div className="flex flex-col gap-3 py-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : editing ? (
          <SellerEditForm
            seller={seller}
            isPending={patch.isPending}
            onSubmit={handleEditSubmit}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <div data-testid="seller-detail" className="flex flex-col gap-4 overflow-y-auto">
            <div className="flex flex-wrap items-center gap-2">
              <GPill tone={verificationTone(seller.verification_status)} size="sm">
                {t(`sellers.verification.${seller.verification_status}`, {
                  defaultValue: seller.verification_status,
                })}
              </GPill>
              {seller.vacation_mode ? (
                <GPill tone="warn" size="sm">
                  {t("sellers.vacationOn")}
                </GPill>
              ) : null}
            </div>

            <dl className="divide-y divide-[var(--g-hairline)]">
              <FieldRow label={t("sellers.detail.businessName")} value={seller.business_name} />
              <FieldRow label={t("sellers.detail.market")} value={seller.market_id} />
              <FieldRow label={t("sellers.detail.pavilion")} value={seller.pavilion_number} />
              <FieldRow label={t("sellers.detail.phone")} value={seller.phone_e164} />
              <FieldRow label={t("sellers.detail.whatsapp")} value={seller.whatsapp_phone_e164} />
              <FieldRow label={t("sellers.detail.telegram")} value={seller.telegram_handle} />
              <FieldRow label={t("sellers.detail.entranceHint")} value={seller.entrance_hint} />
              <FieldRow label={t("sellers.detail.notes")} value={seller.verification_notes} />
            </dl>

            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
                {t("sellers.detail.actions")}
              </h3>
              <div className="flex flex-wrap gap-2">
                {canEdit ? (
                  <Button
                    data-testid="seller-action-edit"
                    variant="primary"
                    size="md"
                    onClick={() => setEditing(true)}
                    className="min-h-[44px]"
                  >
                    {t("sellers.action.edit")}
                  </Button>
                ) : null}
                {canSuspend && !isSuspended ? (
                  <Button
                    data-testid="seller-action-suspend"
                    variant="destructive"
                    size="md"
                    onClick={() => setDialog("suspend")}
                    className="min-h-[44px]"
                  >
                    {t("sellers.action.suspend")}
                  </Button>
                ) : null}
                {canEdit && isSuspended ? (
                  <Button
                    data-testid="seller-action-restore"
                    variant="secondary"
                    size="md"
                    loading={restore.isPending}
                    onClick={handleRestore}
                    className="min-h-[44px]"
                  >
                    {t("sellers.action.restore")}
                  </Button>
                ) : null}
                {canEdit ? (
                  <Button
                    data-testid="seller-action-notify"
                    variant="secondary"
                    size="md"
                    onClick={() => setDialog("notify")}
                    className="min-h-[44px]"
                  >
                    {t("sellers.action.notify")}
                  </Button>
                ) : null}
              </div>
            </section>
          </div>
        )}
      </BottomSheet>

      {seller ? (
        <>
          <SuspendDialog
            open={dialog === "suspend"}
            sellerId={seller.id}
            onClose={() => setDialog(null)}
            onSuccess={() => {
              setDialog(null);
              toast.success(t("sellers.suspend.success"));
            }}
          />
          <NotifyDialog
            open={dialog === "notify"}
            sellerId={seller.id}
            onClose={() => setDialog(null)}
            onSuccess={() => {
              setDialog(null);
              toast.success(t("sellers.notify.success"));
            }}
          />
        </>
      ) : null}
    </>
  );
}

export default SellerDrawer;
